import { useState } from 'react'
import { OccasionalSlotsValidationErrors } from '../../models/occasionalSlotsValidation'
import { AnyFormLayout, FormType } from '../constants/blockByType'
import { OccasionalSlotFormState } from './useOccasionalSlotFormState'
import { useScrollToFirstError } from './useScrollToFirstError'

type FormSetters = Pick<
    OccasionalSlotFormState,
    | 'setOccasionalSlotNumber'
    | 'setSelectedSectionId'
    | 'setConfirmedChildIds'
    | 'setPriorityChildIds'
    | 'setPriorityGroupIds'
>

export function useOccasionalSlotFormHandlers(
    type: FormType,
    layout: AnyFormLayout,
    {
        setOccasionalSlotNumber,
        setSelectedSectionId,
        setConfirmedChildIds,
        setPriorityChildIds,
        setPriorityGroupIds
    }: FormSetters
) {
    const [showMore, setShowMore] = useState<boolean>(type !== 'add')
    const [errors, setErrors] = useState<OccasionalSlotsValidationErrors>({
        date: false,
        time: false,
        count: false,
        children: false,
        section: false
    })
    const [validationAttempt, setValidationAttempt] = useState<number>(0)
    const { registerBlock } = useScrollToFirstError(errors, validationAttempt, [...layout.lines, ...layout.moreLines])

    // Handlers
    function clearErrors(...fields: (keyof OccasionalSlotsValidationErrors)[]): void {
        setErrors((current) => {
            if (!fields.some((field) => current[field])) return current
            const next: OccasionalSlotsValidationErrors = { ...current }
            fields.forEach((field) => {
                next[field] = false
            })
            return next
        })
    }

    function handleSlotCountChange(value: number): void {
        setOccasionalSlotNumber(value)
        clearErrors('count', 'children')
    }

    function handleSelectedSection(value: number | undefined): void {
        setSelectedSectionId(value)
        setConfirmedChildIds([])
        setPriorityChildIds([])
        setPriorityGroupIds([])
        clearErrors('section', 'children')
    }

    function handleValidation(validationErrors: OccasionalSlotsValidationErrors): void {
        setErrors(validationErrors)
        if (Object.values(validationErrors).some(Boolean)) {
            setValidationAttempt((attempt: number) => attempt + 1)
            if (validationErrors.children) setShowMore(true)
        }
    }

    return {
        errors,
        validationAttempt,
        registerBlock,
        showMore,
        setShowMore,
        clearErrors,
        handleSlotCountChange,
        handleSelectedSection,
        handleValidation
    }
}
